import { useState } from 'react';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { extractApiError } from '~/utils/extract-api-error';

import {
  usePostSteps,
  usePostStepDryRun,
  useStartPostStepRetry,
} from '../../hooks/use-post-step-retry';
import type { PostStepName, PostStepScope } from '../../types/post-step-retry.api.types';

import { formatCount } from './batch-retry-utils';
import { classesFor, maxAttemptsOf, STEP_NAME, stepsFor } from './post-step-journey-utils';

export interface PostStepRetryDialogProps {
  jobId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The step whose "Retry" opened the dialog. */
  step: PostStepName;
  /** Preselected scope ("Try once more" opens on the given-up products). */
  initialScope?: PostStepScope;
}

/** The scope radios a step offers, in the order its pills list them, then the given-up ones. */
function scopesFor(step: PostStepName, cap: number): { scope: PostStepScope; label: string }[] {
  const byScope: Partial<Record<PostStepScope, string>> = {};
  for (const c of classesFor(step)) {
    if (
      c.key === 'FAILED' ||
      c.key === 'MISSING' ||
      c.key === 'NO_COVERAGE' ||
      c.key === 'PARTIAL'
    ) {
      byScope[c.key] = c.label;
    }
  }
  return [
    { scope: 'ALL', label: 'Everything not ready' },
    ...(Object.entries(byScope) as [PostStepScope, string][]).map(([scope, label]) => ({
      scope,
      label,
    })),
    // Hidden like any empty scope; the default scopes skip these products.
    { scope: 'GAVE_UP', label: `Gave up after ${String(cap)} tries` },
  ];
}

/** One line under the radios: what the chosen scope costs, and what it means. */
function scopeHint(scope: PostStepScope, cap: number): string | null {
  if (scope === 'NO_COVERAGE') {
    return `Retry these only after adding keywords for them; each try counts toward the limit of ${String(cap)}.`;
  }
  if (scope === 'GAVE_UP') {
    return 'One more try for products that kept failing. If they still fail, accept them as is.';
  }
  return null;
}

/**
 * Retry dialog for a post-step: one "Which products" radio with dry-run counts, plus Start.
 * The body mounts per open, so the dry runs and the selection start fresh each time.
 */
export function PostStepRetryDialog(props: PostStepRetryDialogProps) {
  const { open, onOpenChange } = props;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && <PostStepRetryDialogBody {...props} />}
    </Dialog>
  );
}

function PostStepRetryDialogBody({
  jobId,
  onOpenChange,
  step,
  initialScope,
}: PostStepRetryDialogProps) {
  const [scope, setScope] = useState<PostStepScope>(initialScope ?? 'ALL');
  const { data: postSteps } = usePostSteps(jobId);
  const cap = maxAttemptsOf(postSteps);
  const steps = stepsFor(step, postSteps);
  const options = scopesFor(step, cap);
  const dryRun = usePostStepDryRun(jobId, steps, postSteps !== undefined);
  const { mutate: start, isPending } = useStartPostStepRetry(jobId);

  const loading = postSteps === undefined || dryRun.isLoading;
  const estimates = dryRun.data?.scopes ?? [];
  const rows = options.map((o) => {
    const e = estimates.find((x) => x.scope === o.scope);
    return {
      ...o,
      count: e?.targets ?? 0,
      sdCalls: e?.searchDescriptorCalls ?? 0,
      pdCalls: e?.phraseDescriptionCalls ?? 0,
    };
  });
  // Scopes with nothing in them are hidden; "everything" stays so the dialog is never empty.
  const visible = rows.filter((r) => r.scope === 'ALL' || r.count > 0 || r.scope === scope);
  const selected = visible.find((r) => r.scope === scope) ?? visible[0];
  const blocked = !loading && dryRun.error ? dryRun.error : null;
  const hint = selected ? scopeHint(selected.scope, cap) : null;

  return (
    <DialogContent
      onInteractOutside={(e) => {
        if (isPending) e.preventDefault();
      }}
      onEscapeKeyDown={(e) => {
        if (isPending) e.preventDefault();
      }}
      data-testid="post-step-retry-dialog"
      className="max-w-lg"
    >
      <DialogHeader>
        <DialogTitle data-testid="post-step-retry-dialog-title" className="text-lg font-semibold">
          Retry {STEP_NAME[step].toLowerCase()}
        </DialogTitle>
        <DialogDescription>
          Only this step runs again. Enrichment is not repeated.
        </DialogDescription>
      </DialogHeader>

      <fieldset className="space-y-2">
        <legend className="mb-2 text-sm font-semibold">Which products</legend>
        <RadioGroup
          value={selected?.scope ?? 'ALL'}
          onValueChange={(v) => {
            setScope(v as PostStepScope);
          }}
          className="gap-2"
          aria-label="Which products"
          data-testid="post-step-retry-scope"
        >
          {visible.map((o) => (
            <label
              key={o.scope}
              htmlFor={`post-step-scope-${o.scope}`}
              className="hover:border-primary/50 has-[:checked]:border-primary has-[:checked]:bg-primary/5 flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5"
            >
              <RadioGroupItem
                id={`post-step-scope-${o.scope}`}
                data-testid={`post-step-scope-${o.scope.toLowerCase()}`}
                value={o.scope}
                className="size-4"
              />
              <span className="flex-1 text-sm font-medium">{o.label}</span>
              <span className="text-muted-foreground text-sm tabular-nums">
                {loading ? '…' : o.count.toLocaleString()}
              </span>
            </label>
          ))}
        </RadioGroup>
      </fieldset>

      {selected && !loading && selected.count > 0 && (
        <p data-testid="post-step-retry-calls" className="text-muted-foreground text-xs">
          Runs {formatCount(selected.sdCalls)} search-descriptor and {formatCount(selected.pdCalls)}{' '}
          phrase-description generations.
        </p>
      )}
      {hint && (
        <p data-testid="post-step-retry-hint" className="text-muted-foreground text-xs">
          {hint}
        </p>
      )}

      {blocked && (
        <p data-testid="post-step-retry-blocked" role="alert" className="text-error text-sm">
          {extractApiError(blocked, 'Nothing can be retried right now.')}
        </p>
      )}

      <DialogFooter className="gap-2">
        <Button
          variant="outline"
          data-testid="post-step-retry-cancel"
          className="cursor-pointer"
          disabled={isPending}
          onClick={() => {
            onOpenChange(false);
          }}
        >
          Cancel
        </Button>
        <Button
          data-testid="post-step-retry-start"
          className="cursor-pointer"
          isLoading={isPending}
          disabled={isPending || loading || !selected || selected.count === 0}
          onClick={() => {
            if (!selected) return;
            start(
              { steps, scope: selected.scope },
              {
                onSuccess: () => {
                  onOpenChange(false);
                },
              },
            );
          }}
        >
          {loading ? 'Loading…' : `Start retry · ${(selected?.count ?? 0).toLocaleString()}`}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
